/**
 * Only lexicalDistance is publicly available, others exports are internals, don't use them if MPA does not explicitly ask you to do so!
 * If you really want to dig into this file, read this before: https://www.amazon.fr/Compilateurs-principes-techniques-outils-%C3%A9dition/dp/2744070378
 **/
import * as E from '../errors';

export * as lexicalDistance from './lexicalDistance';

/**
 * @internal
 **/
export interface Unit {
	name: string;
};

/**
 * @internal
 **/
export interface IUniLex extends Unit {
	symbol: string;
	isMergeable: boolean;
};

/**
 * @internal
 **/
export function createUniLex(
	name: string,
	symbol: string = '',
	isMergeable: boolean = false
): IUniLex {
	return ({
		name,
		symbol,
		isMergeable
	});
};
/**
 * @internal
 **/
export const unknownUniLex = createUniLex('unknown', '', true);
/**
 * @internal
 **/
export const newLineUniLex = createUniLex('newLine', '\n', true);
/**
 * @internal
 **/
export type TTokenParameters<TValue> = [
	TValue,
	number,
	number,
	TokenList,
	boolean
];

/**
 * @internal
 **/
export abstract class Token<TValue> implements Unit {
	readonly abstract name: string;
	parent: TokenList | null;
	value: TValue;
	position: {
		x: number;
		y: number;
	};

	validate() {
		const list = this.getTokensList();
		for (const tl of list) {
			tl.validate();
		}
	};

	previous(): Token<any> | null {
		if (!this.parent) {
			throw E.user('', {});
		}
		const i = this.parent.tokens.indexOf(this) - 1;
		if (i < 0) {
			return (null);
		}
		return (this.parent.tokens[i] as Token<any>);
	};

	next(): Token<any> | null {
		if (!this.parent) {
			throw E.user('', {});
		}
		const i = this.parent.tokens.indexOf(this) + 1;
		if (i >= this.parent.tokens.length) {
			return (null);
		}
		return (this.parent.tokens[i] as Token<any>);
	};

	remove() {
		if (!this.parent) {
			throw new RangeError;
		}
		this.parent.remove(this);
	};

	getTokensList(): TokenList[] {
		return ([]);
	};

	visit(
		visitor: (t: Token<any>) => boolean | void
	): boolean {
		const r = visitor(this) === true;
		if (r) {
			return (r);
		}
		const list = this.getTokensList();
		for (const tl of list) {
			const r = tl.visit(visitor);
			if (r) {
				return (r);
			}
		}
		return (false);
	};

	replaceBy(token: Token<any>) {
		if (!this.parent) {
			throw new RangeError;
		}
		this.parent.replaceBy(this, token);
	};

	simplifyValue(): any {
		return (this.value);
	};

	simplify() {
		const simplifiedName = (
			(this instanceof LexicalToken) ?
				(this.name + '-' + this.value.unit.name) :
				this.name
		);
		const out = {
			name: simplifiedName,
			value: this.simplifyValue()
		};
		return (out);
	};

	constructor(
		value: TValue,
		posX: number,
		posY: number,
		parent: TokenList,
		doNotAdd: boolean = false
	) {
		this.parent = parent;
		this.value = value;
		this.position = {
			x: posX,
			y: posY
		};
		if (doNotAdd !== true) {
			this.parent.add(this);
		}
	};
};

/**
 * @internal
 **/
export class LexicalToken extends Token<{
	text: string;
	unit: IUniLex;
}> {
	name = 'lex';

	override toString() {
		return (this.value.text);
	}

	is(unit: string, text: string) {
		return (
			(this.value.unit.name === unit) &&
			(this.value.text === text)
		);
	};

	isA(unit: string) {
		return (this.value.unit.name === unit);
	};

	transform(unit: IUniLex, text: string) {
		this.value.unit = unit;
		this.value.text = text;
	};

	override simplifyValue() {
		return (super.simplifyValue().text);
	};
};

/**
 * @internal
 **/
export abstract class UnaryToken extends Token<{ target: TokenList }> {
	name = 'unary';

	override getTokensList(): TokenList[] {
		return ([this.value.target]);
	};

	override simplifyValue() {
		const s = super.simplifyValue();
		return ({
			target: s.target.tokens.map((x: any) => x.simplify())
		});
	};
};

/**
 * @internal
 **/
export abstract class BinaryToken extends Token<{
	LHS: TokenList;
	RHS: TokenList;
}> {
	name = 'binary';

	override getTokensList(): TokenList[] {
		return ([this.value.LHS, this.value.RHS]);
	};

	override simplifyValue() {
		const s = super.simplifyValue();
		return ({
			LHS: s.LHS.tokens.map((x: any) => x.simplify()),
			RHS: s.RHS.tokens.map((x: any) => x.simplify())
		});
	};
};

/**
 * @internal
 **/
export abstract class BlockToken extends Token<{ tokens: TokenList }> {
	name = 'block';

	override getTokensList(): TokenList[] {
		return ([this.value.tokens]);
	};

	override simplifyValue() {
		const s = super.simplifyValue();
		return (s.tokens.tokens.map((x: any) => x.simplify()));
	};
};

/**
 * @internal
 **/
export class TokenList {
	static from(token: Token<any>) {
		const out = new TokenList;
		out.add(token);
		return (out);
	};

	tokens: Token<any>[] = [];

	validate() {
		for (const token of this.tokens) {
			if (token.parent !== this) {
				throw E.user('SyntaxError: ', {
					tokenList: this,
					token
				});
			}
			token.validate();
		}
	};

	add(token: Token<any>) {
		if (token.parent) {
			token.remove();
		}
		this.tokens.push(token);
		token.parent = this;
	};

	remove(token: Token<any>) {
		this.filter(x => x !== token);
		token.parent = null;
	};

	getLexicalTokens() {
		return (
			this.tokens.filter(x => x instanceof LexicalToken) as LexicalToken[]
		);
	};

	filter(...p: Parameters<any[]['filter']>) {
		this.tokens = this.tokens.filter(...p);
	};

	visit(
		visitor: (t: Token<any>) => boolean | void
	): boolean {
		for (const t of this.tokens) {
			const isShouldStop = t.visit(visitor);
			if (isShouldStop === true) {
				return (isShouldStop);
			}
		}
		return (false);
	};

	replaceBy(old: Token<any>, _new: Token<any>) {
		this.tokens = this.tokens.map(x => x === old ? _new : x);
	};

	parseBlocks(
		Block: {
			new(
				value: { tokens: TokenList },
				posX: number,
				posY: number,
				parent: TokenList,
				doNotAdd?: boolean
			): BlockToken;
		},
		whatItIs: (token: Token<any>) => 'open' | 'close' | 'unknown',
		onBlock: (bt: BlockToken) => void
	) {
		const blocks: BlockToken[] = [];
		let block: BlockToken | null = null;
		for (const token of this.tokens) {
			const result = whatItIs(token);
			switch (result) {
				case 'open' : {
					if (block) {
						const parentBlock: BlockToken = block;
						blocks.push(parentBlock);
						block = new Block(
							{
								tokens: new TokenList()
							},
							token.position.x,
							token.position.y,
							parentBlock.getTokensList()[0] as TokenList,
							false
						);
					} else {
						block = new Block(
							{
								tokens: new TokenList()
							},
							token.position.x,
							token.position.y,
							this,
							true
						);
						token.replaceBy(block);
					}
					block.value.tokens.add(token);
				}
					break;
				case 'unknown' : {
					if (block) {
						block.value.tokens.add(token);
					}
				}
					break;
				case 'close' : {
					if (!block) {
						throw E.user('SyntaxError: ', {});
					}
					block.value.tokens.add(token);
					onBlock(block);
					block = blocks.pop() || null;
				}
					break;
			}
		}
		if (block) {
			throw E.user('SyntaxError: ', {});
		}
	};

	parseDelimited(
		whatItIs: (token: Token<any>) => 'openclose' | 'open' | 'close' | 'unknown',
		create: (
			parent: TokenList,
			position: { x: number; y: number },
			content: Token<any>[]
		) => Token<unknown>
	) {
		const currentMatch = [];
		let first: Token<any> | null = null;
		for (const token of this.tokens) {
			const result = whatItIs(token);
			switch (result) {
				case 'openclose' : {
					if (first) {
						currentMatch.push(token);
						const newToken = create(this, first.position, currentMatch);
						first.replaceBy(newToken);
						first.remove();
						token.remove();
						first = null;
						currentMatch.length = 0;
					} else {
						currentMatch.push(token);
						first = token;
					}
				}
					break;
				case 'unknown' : {
					if (first) {
						currentMatch.push(token);
						token.remove();
					}
				}
					break;
				// case 'open' : {
				// 	if (first) {
				// 		throw E.user(SyntaxError, ``, {
				// 		});
				// 	}
				// 	currentMatch.push(token);
				// 	first = token;
				// } break;
				// case 'close' : {
				// 	if (!first) {
				// 		throw E.user(SyntaxError, ``, {
				// 		});
				// 	}
				// 	create(this, first.position, currentMatch);
				// 	currentMatch.length = 0;
				// 	first = null;
				// } break;
			}
		}
		if (first) {
			throw E.user('SyntaxError: ', {});
		}
	};

	constructor(tokens?: Token<any>[]) {
		if (tokens) {
			for (const token of tokens) {
				this.add(token);
			}
		}
	};
};

/**
 * @internal
 **/
export class PersistentTokenListSealed extends TokenList {
	static create(tokens: Token<any>[] = []): PersistentTokenListSealed {
		if (
			(tokens.length === 0) ||
			tokens.every(x => !(x.parent instanceof PersistentTokenListSealed))
		) {
			return (new PersistentTokenListSealed(tokens));
		}
		if (tokens.every(x => x.parent instanceof PersistentTokenListSealed)) {
			return (<PersistentTokenListSealed>(<Token<any>>tokens[0]).parent);
		}
		throw new Error('Cannot mix PersistentTokenListSealed with TokenList!');
	};

	private isConstructed = false;

	override add(token: Token<any>) {
		if (this.isConstructed) {
			throw new Error('Cannot add a token to a sealed token list!');
		}
		super.add(token);
	};

	override remove(token: Token<any>) {
		void token;
		throw new Error('Cannot remove a token from a sealed token list!');
	};

	override filter(...p: Parameters<any[]['filter']>) {
		void p;
		throw new Error('Cannot filter a sealed token list!');
	};

	override replaceBy(old: Token<any>, _new: Token<any>) {
		void old;
		void _new;
		throw new Error('Cannot filter a sealed token list!');
	};

	constructor(tokens?: Token<any>[]) {
		super(tokens);
		this.isConstructed = true;
	};
};

/**
 * @internal
 **/
export class CompilationUnit {
	readonly source: string;
	tokens = new TokenList;

	validate() {
		this.tokens.validate();
	};

	visit(
		visitor: (t: Token<any>) => boolean | void
	) {
		this.tokens.visit(visitor);
	};

	getAllTokensList() {
		const all: TokenList[] = [this.tokens];
		this.visit((token) => {
			const list = token.getTokensList();
			for (const tl of list) {
				all.push(tl);
			}
		});
		return (all);
	};

	constructor(source: string) {
		this.source = source;
	};
};

/**
 * @internal
 **/
export function lexer(
	source: string,
	unilexs: IUniLex[]
): CompilationUnit {
	const cu = new CompilationUnit(source);
	let lt: LexicalToken | null = null;
	let x = 0;
	let y = 0;
	for (const c of source) {
		if (lt && (lt.value.unit.name === 'newLine')) {
			y++;
			x = 0;
		}
		const u = unilexs.find(x => x.symbol === c) || unknownUniLex;
		if (
			lt &&
			(lt.value.unit === u) &&
			(lt.value.unit.isMergeable)
		) {
			lt.value.text += c;
		} else {
			lt = new LexicalToken({
				text: c,
				unit: u
			}, x, y, cu.tokens);
			cu.tokens.add(lt);
		}
		x++;
	}
	return (cu);
};
